import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DataTable, { Column } from '../../components/common/DataTable';
import { AppWhitelist, WhitelistFormData } from '../../types/analysis';
import {
  getWhitelist,
  createWhitelist,
  updateWhitelist,
  deleteWhitelist,
  uploadWhitelistCsv,
  CsvUploadResult,
} from '../../services/api/securityApi';

const WhitelistPage: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    search?: string;
    category?: string;
  }>({
    page: 0,
    limit: 20,
  });

  const [searchInput, setSearchInput] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AppWhitelist | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    itemId: number | null;
  }>({
    open: false,
    itemId: null,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // CSV 업로드 관련 상태
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvResult, setCsvResult] = useState<CsvUploadResult | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WhitelistFormData>({
    defaultValues: {
      packageName: '',
      appName: '',
      developer: '',
      category: '',
      description: '',
      source: '',
    },
  });

  // Queries
  const { data, isLoading, error } = useQuery({
    queryKey: ['whitelist', filters],
    queryFn: () => getWhitelist(filters),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: createWhitelist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      setSnackbar({
        open: true,
        message: '화이트리스트에 추가되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '화이트리스트 추가에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<WhitelistFormData> }) =>
      updateWhitelist(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      setSnackbar({
        open: true,
        message: '화이트리스트가 수정되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '화이트리스트 수정에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWhitelist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      setSnackbar({
        open: true,
        message: '화이트리스트에서 제거되었습니다.',
        severity: 'success',
      });
      setConfirmDialog({ open: false, itemId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '화이트리스트 제거에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  // CSV 업로드 mutation
  const csvUploadMutation = useMutation({
    mutationFn: uploadWhitelistCsv,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      setCsvResult(result);
      setSnackbar({
        open: true,
        message: `${result.success}개 항목이 등록되었습니다.`,
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'CSV 업로드에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  // Event handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = () => {
    setFilters(prev => ({
      ...prev,
      search: searchInput || undefined,
      page: 0,
    }));
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleCategoryFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      category: value === 'all' ? undefined : value,
      page: 0,
    }));
  };

  const handleOpenAddDialog = () => {
    setEditingItem(null);
    reset({
      packageName: '',
      appName: '',
      developer: '',
      category: '',
      description: '',
      source: '',
    });
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (item: AppWhitelist) => {
    setEditingItem(item);
    reset({
      packageName: item.packageName,
      appName: item.appName || '',
      developer: item.developer || '',
      category: item.category || '',
      description: item.description || '',
      source: item.source || '',
    });
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingItem(null);
    reset();
  };

  const handleFormSubmit = (data: WhitelistFormData) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, itemId: id });
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.itemId) {
      deleteMutation.mutate(confirmDialog.itemId);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  // CSV 관련 핸들러
  const handleCsvUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      csvUploadMutation.mutate(file);
      setCsvDialogOpen(true);
    }
    // 같은 파일 재선택 가능하도록 초기화
    event.target.value = '';
  };

  const handleDownloadTemplate = () => {
    const csvContent = '앱명,패키지명,제조사명\n카카오톡,com.kakao.talk,카카오\n네이버,com.nhn.android.search,네이버';
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'whitelist_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCloseCsvDialog = () => {
    setCsvDialogOpen(false);
    setCsvResult(null);
  };

  const columns: Column<AppWhitelist>[] = [
    {
      id: 'packageName',
      label: '패키지명',
      minWidth: 200,
    },
    {
      id: 'appName',
      label: '앱 이름',
      minWidth: 150,
      format: (value: string | null) => value || '-',
    },
    {
      id: 'developer',
      label: '개발자',
      minWidth: 120,
      format: (value: string | null) => value || '-',
    },
    {
      id: 'category',
      label: '카테고리',
      minWidth: 100,
      format: (value: string | null) => (
        value ? (
          <Chip
            label={value}
            size="small"
            color={
              value === '은행' ? 'primary' :
              value === '정부' ? 'secondary' :
              value === '보안' ? 'success' : 'default'
            }
          />
        ) : '-'
      ),
    },
    {
      id: 'verifiedDate',
      label: '검증일',
      minWidth: 120,
      format: (value: Date | null) => 
        value ? new Date(value).toLocaleDateString('ko-KR') : '-',
    },
    {
      id: 'isActive',
      label: '상태',
      minWidth: 80,
      align: 'center',
      format: (value: boolean) => (
        <Chip
          label={value ? '활성' : '비활성'}
          size="small"
          color={value ? 'success' : 'default'}
        />
      ),
    },
    {
      id: 'actions',
      label: '작업',
      minWidth: 150,
      align: 'center',
      format: (_value: any, row: AppWhitelist) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" variant="outlined" onClick={() => handleOpenEditDialog(row)}>
            수정
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDeleteClick(row.id)}
          >
            제거
          </Button>
        </Stack>
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">데이터를 불러오는데 실패했습니다.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        앱 화이트리스트 관리
      </Typography>

      {/* 필터 및 검색 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="검색 (패키지명, 앱명, 개발자)"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            size="small"
            sx={{ minWidth: 300 }}
            placeholder="검색어를 입력하세요"
          />
          <Button variant="contained" onClick={handleSearchSubmit}>
            검색
          </Button>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel size="small">카테고리</InputLabel>
            <Select
              value={filters.category || 'all'}
              label="카테고리"
              onChange={e => handleCategoryFilterChange(e.target.value)}
              size="small"
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="은행">은행</MenuItem>
              <MenuItem value="정부">정부</MenuItem>
              <MenuItem value="보안">보안</MenuItem>
              <MenuItem value="통신">통신</MenuItem>
              <MenuItem value="기타">기타</MenuItem>
            </Select>
          </FormControl>

          {(filters.search || filters.category) && (
            <Button
              variant="outlined"
              onClick={() => {
                setSearchInput('');
                setFilters(prev => ({ 
                  ...prev, 
                  search: undefined, 
                  category: undefined, 
                  page: 0 
                }));
              }}
            >
              초기화
            </Button>
          )}
        </Stack>
      </Paper>

      {/* 추가 버튼 */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            화이트리스트 추가
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleCsvUploadClick}
          >
            CSV 일괄 등록
          </Button>
          <Button
            variant="text"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            템플릿 다운로드
          </Button>
        </Stack>
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>

      {/* 데이터 테이블 */}
      <DataTable
        columns={columns}
        data={data?.items || []}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        loading={isLoading}
      />

      {/* 추가/수정 다이얼로그 */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? '화이트리스트 수정' : '화이트리스트 추가'}
        </DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              <Controller
                name="packageName"
                control={control}
                rules={{ required: '패키지명은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="패키지명"
                    fullWidth
                    error={!!errors.packageName}
                    helperText={errors.packageName?.message}
                    placeholder="com.example.app"
                  />
                )}
              />

              <Controller
                name="appName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="앱 이름" fullWidth />
                )}
              />

              <Controller
                name="developer"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="개발자/회사" fullWidth />
                )}
              />

              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>카테고리</InputLabel>
                    <Select {...field} label="카테고리">
                      <MenuItem value="">선택 안함</MenuItem>
                      <MenuItem value="은행">은행</MenuItem>
                      <MenuItem value="정부">정부</MenuItem>
                      <MenuItem value="보안">보안</MenuItem>
                      <MenuItem value="통신">통신</MenuItem>
                      <MenuItem value="기타">기타</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="설명"
                    fullWidth
                    multiline
                    rows={3}
                  />
                )}
              />

              <Controller
                name="source"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="출처" fullWidth />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog}>취소</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingItem ? '수정' : '추가'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, itemId: null })}>
        <DialogTitle>화이트리스트 제거</DialogTitle>
        <DialogContent>
          <Typography>
            이 앱을 화이트리스트에서 제거하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, itemId: null })}>
            취소
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            제거
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* CSV 업로드 결과 다이얼로그 */}
      <Dialog open={csvDialogOpen} onClose={handleCloseCsvDialog} maxWidth="sm" fullWidth>
        <DialogTitle>CSV 일괄 등록 결과</DialogTitle>
        <DialogContent>
          {csvUploadMutation.isPending && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>업로드 중...</Typography>
              <LinearProgress />
            </Box>
          )}
          {csvResult && (
            <Box>
              <Alert severity={csvResult.success > 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                총 {csvResult.total}개 중 {csvResult.success}개 등록 완료
                {(csvResult.csvDuplicate > 0 || csvResult.dbDuplicate > 0) && (
                  <Box component="span">
                    {' ('}
                    {csvResult.csvDuplicate > 0 && `CSV 내 중복 ${csvResult.csvDuplicate}개`}
                    {csvResult.csvDuplicate > 0 && csvResult.dbDuplicate > 0 && ', '}
                    {csvResult.dbDuplicate > 0 && `DB 중복 ${csvResult.dbDuplicate}개`}
                    {' 스킵)'}
                  </Box>
                )}
              </Alert>
              {csvResult.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>오류 내역:</Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1 }}>
                    {csvResult.errors.map((err, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`행 ${err.row}: ${err.message}`}
                          secondary={err.data?.packageName || ''}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCsvDialog} disabled={csvUploadMutation.isPending}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhitelistPage;
